import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Wand2, Eye, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RelicCodeGenerator = () => {
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewCodes, setPreviewCodes] = useState([]);
  const [previewFormat, setPreviewFormat] = useState('');
  const [generationResult, setGenerationResult] = useState(null);
  const [selectedBatchFormat, setSelectedBatchFormat] = useState('');
  
  const [formData, setFormData] = useState({
    batch_id: '',
    product_id: '',
    quantities: {
      COMMON: 0,
      UNCOMMON: 0,
      RARE: 0,
      LEGENDARY: 0
    }
  });

  useEffect(() => {
    fetchBatches();
    fetchProducts();
  }, []);

  const fetchBatches = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/relic/batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(response.data.filter(b => b.is_active));
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handlePreview = async () => {
    if (!formData.batch_id) {
      alert('Please select a batch first');
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_URL}/api/admin/relic/codes/preview`,
        { batch_id: formData.batch_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewCodes(response.data.codes);
      setPreviewFormat(response.data.code_format);
    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Failed to generate preview');
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!formData.batch_id) {
      alert('Please select a batch');
      return;
    }

    const total = Object.values(formData.quantities).reduce((a, b) => a + b, 0);
    if (total === 0) {
      alert('Please enter quantities for at least one rarity');
      return;
    }

    if (!window.confirm(`Generate ${total} codes?`)) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_URL}/api/admin/relic/codes/generate`,
        {
          batch_id: formData.batch_id,
          product_id: formData.product_id || null,
          quantities: formData.quantities
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGenerationResult(response.data);
      setFormData({
        batch_id: '',
        product_id: '',
        quantities: { COMMON: 0, UNCOMMON: 0, RARE: 0, LEGENDARY: 0 }
      });
    } catch (error) {
      console.error('Error generating codes:', error);
      alert('Failed to generate codes');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (rarity, value) => {
    const num = parseInt(value) || 0;
    setFormData({
      ...formData,
      quantities: {
        ...formData.quantities,
        [rarity]: Math.max(0, num)
      }
    });
  };

  const getTotalQuantity = () => {
    return Object.values(formData.quantities).reduce((a, b) => a + b, 0);
  };

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6">Code Generator</h1>

      {generationResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-bold text-green-900 mb-2">✅ {generationResult.message}</h3>
              <div className="text-sm text-green-800 space-y-1">
                <div>Total Created: <span className="font-semibold">{generationResult.total_created}</span></div>
                {Object.entries(generationResult.counts).map(([rarity, count]) => (
                  <div key={rarity}>
                    {rarity}: <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => setGenerationResult(null)}
              className="text-green-600 hover:text-green-800"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border rounded-lg p-6 mb-6">
        <form onSubmit={handleGenerate} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Batch *</label>
              <select
                value={formData.batch_id}
                onChange={(e) => {
                  const batchId = e.target.value;
                  setFormData({ ...formData, batch_id: batchId });
                  const selectedBatch = batches.find(b => b.id === batchId);
                  if (selectedBatch) {
                    setSelectedBatchFormat(selectedBatch.code_format || 'AAA-AAA');
                  } else {
                    setSelectedBatchFormat('');
                  }
                }}
                className="w-full border rounded-lg px-3 py-2"
                required
              >
                <option value="">Select batch...</option>
                {batches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name} {batch.code_format ? `(${batch.code_format})` : ''}
                  </option>
                ))}
              </select>
              {selectedBatchFormat && (
                <p className="text-xs text-blue-600 mt-1 font-mono">
                  Codes will use format: {selectedBatchFormat}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Product (optional)
              </label>
              <select
                value={formData.product_id}
                onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">None</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">Quantities by Rarity</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['COMMON', 'UNCOMMON', 'RARE', 'LEGENDARY'].map((rarity) => (
                <div key={rarity}>
                  <label className="block text-sm font-medium mb-1 capitalize">
                    {rarity.toLowerCase()}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantities[rarity]}
                    onChange={(e) => handleQuantityChange(rarity, e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              ))}
            </div>
            <div className="mt-3 text-sm text-gray-600">
              Total: <span className="font-semibold">{getTotalQuantity()}</span> codes
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handlePreview}
              className="flex items-center space-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              <Eye className="w-4 h-4" />
              <span>Preview Format</span>
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              <Wand2 className="w-4 h-4" />
              <span>{loading ? 'Generating...' : 'Generate Codes'}</span>
            </button>
          </div>
        </form>
      </div>

      {previewCodes.length > 0 && (
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-3">Preview Sample Codes</h3>
          <p className="text-sm text-gray-600 mb-4">
            Format: <span className="font-mono font-semibold">{previewFormat}</span>
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 font-mono text-sm">
            {previewCodes.map((code, idx) => (
              <div key={idx} className="bg-gray-50 px-3 py-2 rounded border text-center">
                {code}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
